import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface CookieHintProps {
  onMoreInfo: () => void;
}

const STORAGE_KEY = "rankbet_cookie_hint_dismissed";

const FONT =
  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

// Minimal cookie hint banner. We only use essential session cookies (the
// supabase auth token in localStorage), so GDPR doesn't strictly require an
// opt-in banner — but a short transparency note is good practice.
const CookieHint: React.FC<CookieHintProps> = ({ onMoreInfo }) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        // Small delay so it doesn't pop in immediately on first paint
        const tm = setTimeout(() => setVisible(true), 600);
        return () => clearTimeout(tm);
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      // ignore
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie hint"
      style={{
        position: "fixed",
        bottom: "16px",
        left: "16px",
        right: "16px",
        zIndex: 2000,
        maxWidth: "640px",
        margin: "0 auto",
        background:
          "linear-gradient(180deg, rgba(20,22,40,0.97) 0%, rgba(15,17,30,0.97) 100%)",
        border: "1px solid rgba(102, 126, 234, 0.4)",
        borderRadius: "14px",
        padding: "16px 20px",
        color: "white",
        fontFamily: FONT,
        boxShadow:
          "0 0 0 1px rgba(34, 211, 238, 0.1) inset, 0 20px 60px -20px rgba(0, 0, 0, 0.6)",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: "14px",
          lineHeight: 1.55,
          color: "rgba(255,255,255,0.85)",
        }}
      >
        {t("cookieHint.body")}
      </p>
      <div
        style={{
          display: "flex",
          gap: "8px",
          justifyContent: "flex-end",
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          onClick={onMoreInfo}
          style={{
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.18)",
            color: "rgba(255,255,255,0.85)",
            padding: "8px 14px",
            borderRadius: "8px",
            fontSize: "13px",
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: FONT,
          }}
        >
          {t("cookieHint.moreInfo")}
        </button>
        <button
          type="button"
          onClick={dismiss}
          style={{
            background: "linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)",
            border: "none",
            color: "white",
            padding: "8px 18px",
            borderRadius: "8px",
            fontSize: "13px",
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: FONT,
          }}
        >
          {t("cookieHint.ok")}
        </button>
      </div>
    </div>
  );
};

export default CookieHint;
